import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getAssistantId,
  getVectorStoreId,
  createChatSession,
  updateChatSession,
  createThread,
  addMessageToThread,
  runAssistant,
  checkRunStatus,
  listMessages,
  saveChatMessage,
  getChatMessages,
  getChatSessions,
  submitToolOutputs
} from '../../../api';
import { mapChatSessionToPanel, sortChatMessages } from '../../../features/sentiment';
import { INITIAL_CHAT_METADATA } from '../constants/chatMetadataDefaults';
import { setupAssistantPrompt } from '../utilities';
import { executeFunction, getToolsArray } from '../functionCalls';

const createThreadWithRetry = async (retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await createThread();
    } catch (error) {
      process.env.NODE_ENV === 'development' && console.error(`Thread creation attempt ${i + 1} failed:`, error);
      if (i === retries - 1) throw error;
      await new Promise((resolve) => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
  return null;
};

const useAssistantChat = ({
  userId,
  activeSessionIdRef,
  fetchSessionsListRef,
  refreshContext,
  workspaceId
}) => {
  const { t } = useTranslation();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [textareaHeight, setTextareaHeight] = useState('auto');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedCitation, setSelectedCitation] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isOwner, setIsOwner] = useState(true);
  const [chatMetadata, setChatMetadata] = useState(INITIAL_CHAT_METADATA);
  const [sessionSwitchLoading, setSessionSwitchLoading] = useState(false);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textAreaRef = useRef(null);
  const loadedSessionIdRef = useRef(null);
  const loadingSessionIdRef = useRef(null);
  const sessionLoadSeqRef = useRef(0);
  const userIdRef = useRef(userId);
  const messagesRef = useRef(messages);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const resetSession = useCallback(() => {
    loadedSessionIdRef.current = null;
    setMessages([]);
    setChatMetadata(INITIAL_CHAT_METADATA);
    setThreadId(null);
  }, []);

  const loadSpecificSession = useCallback(async (sessionId, { isInitial = false } = {}) => {
    if (!sessionId || sessionId === 'new') return;
    if (loadedSessionIdRef.current === sessionId) return;
    if (loadingSessionIdRef.current === sessionId) return;

    loadingSessionIdRef.current = sessionId;
    const seq = ++sessionLoadSeqRef.current;
    if (!isInitial) {
      setSessionSwitchLoading(true);
    }
    setChatLoading(true);

    try {
      const [sessionData, messagesData] = await Promise.all([
        getChatSessions(1, sessionId),
        getChatMessages(sessionId)
      ]);
      if (seq !== sessionLoadSeqRef.current) return;

      if (Array.isArray(sessionData) && sessionData.length > 0) {
        const session = sessionData[0];
        setThreadId(session.thread_id);

        if (Array.isArray(messagesData) && messagesData.length > 0) {
          const sortedMessages = sortChatMessages(messagesData);
          setMessages(sortedMessages.map((message) => ({
            text: message.content,
            sender: message.role,
            role: message.role,
            order: message.order,
            valence: message.valence,
            date_created: message.date_created
          })));
        } else {
          setMessages([{
            text: t('assistant.inbox.welcomeMessage'),
            sender: 'assistant',
            date_created: new Date().toISOString()
          }]);
        }

        const currentUserId = userIdRef.current || localStorage.getItem('user_id');
        const { chatMetadata: nextMetadata, isOwner: sessionIsOwner } = mapChatSessionToPanel(session, currentUserId);
        setIsOwner(sessionIsOwner);
        setChatMetadata(nextMetadata);
        loadedSessionIdRef.current = sessionId;
      }
    } catch (error) {
      process.env.NODE_ENV === 'development' && console.error('Error loading specific session:', error);
    } finally {
      if (loadingSessionIdRef.current === sessionId) {
        loadingSessionIdRef.current = null;
      }
      if (seq === sessionLoadSeqRef.current) {
        setChatLoading(false);
        setSessionSwitchLoading(false);
      }
    }
  }, [t]);

  const handleCreateNewChat = useCallback(async () => {
    try {
      setIsInitializing(true);
      const assistantId = await getAssistantId();
      const vectorStoreId = await getVectorStoreId();
      const context = await refreshContext();
      const resolvedWorkspaceId = context.workspaceId || workspaceId;

      if (!resolvedWorkspaceId) throw new Error('No workspace ID found');

      const newThreadId = await createThread();
      if (!newThreadId) throw new Error('Thread creation failed');

      const newSession = await createChatSession(
        'New Chat',
        newThreadId,
        assistantId,
        vectorStoreId,
        resolvedWorkspaceId
      );

      const welcomeMsg = t('assistant.inbox.welcomeMessage');

      setThreadId(newThreadId);
      setMessages([{ text: welcomeMsg, sender: 'assistant', date_created: new Date().toISOString() }]);
      setIsOwner(true);
      loadedSessionIdRef.current = newSession.id;

      await setupAssistantPrompt(newThreadId);
      await addMessageToThread(newThreadId, { role: 'assistant', content: welcomeMsg });
      await saveChatMessage(newSession.id, { role: 'assistant', content: welcomeMsg, order: 1 });

      fetchSessionsListRef.current({ resetPage: true });

      return newSession;
    } catch (error) {
      process.env.NODE_ENV === 'development' && console.error('Session creation error:', error);
      return null;
    } finally {
      setIsInitializing(false);
    }
  }, [t, refreshContext, workspaceId, fetchSessionsListRef]);

  const handleSourceClick = useCallback(({ source, citation }) => {
    setSelectedSource(source);
    setSelectedCitation(citation);
    setDrawerOpen(true);
  }, []);

  const smartScrollToLatestMessage = useCallback(() => {
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer && !userHasScrolledUp) {
      setTimeout(() => {
        const messageElements = messagesContainer.querySelectorAll('.message-item');
        const lastMessage = messageElements[messageElements.length - 1];

        if (lastMessage) {
          const containerRect = messagesContainer.getBoundingClientRect();
          const messageRect = lastMessage.getBoundingClientRect();
          const scrollToPosition = messagesContainer.scrollTop + (messageRect.top - containerRect.top) - 10;
          messagesContainer.scrollTo({
            top: Math.max(0, scrollToPosition),
            behavior: 'smooth'
          });
        }
      }, 50);
    }
  }, [userHasScrolledUp]);

  const handleUserScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setUserHasScrolledUp(!isNearBottom);
  }, []);

  const adjustTextAreaHeight = useCallback((reset = false) => {
    if (!textAreaRef.current) return;

    if (reset) {
      textAreaRef.current.style.height = 'auto';
      setTextareaHeight('auto');
      return;
    }

    const scrollHeight = textAreaRef.current.scrollHeight;
    setTextareaHeight(`${Math.min(scrollHeight, 80)}px`);
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!input.trim()) return;

    const userMessage = input;
    setIsProcessing(true);
    setMessages((prev) => [...prev, {
      text: userMessage,
      sender: 'user',
      date_created: new Date().toISOString()
    }]);

    try {
      const assistantId = await getAssistantId();
      const vectorStoreId = await getVectorStoreId();

      const activeSessionId = activeSessionIdRef.current;
      if (!threadId || !activeSessionId) {
        throw new Error('Thread or session not initialized');
      }

      const currentMessages = messagesRef.current;
      let activeThreadId = threadId;
      const messageResponse = await addMessageToThread(activeThreadId, {
        role: 'user',
        content: userMessage
      });

      if (messageResponse.new_thread_id) {
        const previousMessages = currentMessages.filter((m) => m.sender !== 'system');
        for (const msg of previousMessages) {
          await addMessageToThread(messageResponse.new_thread_id, {
            role: msg.sender,
            content: msg.text
          });
        }
        activeThreadId = messageResponse.new_thread_id;
        setThreadId(activeThreadId);
      }

      await saveChatMessage(activeSessionId, {
        role: 'user',
        content: userMessage,
        order: currentMessages.length
      });

      setInput('');
      adjustTextAreaHeight(true);

      const tools = await getToolsArray(true);
      const runId = await runAssistant(activeThreadId, assistantId, {
        tools,
        file_search: vectorStoreId ? { vector_store_ids: [vectorStoreId] } : undefined,
        instructions: 'Please provide a helpful response based on the context. You can use function calls when appropriate.'
      });

      let runStatus;
      let statusCheckCount = 0;
      const maxStatusChecks = 30;

      do {
        if (statusCheckCount >= maxStatusChecks) {
          throw new Error('Assistant run timed out after maximum attempts');
        }

        const runData = await checkRunStatus(activeThreadId, runId);
        runStatus = runData.status;

        if (runStatus === 'failed' || runStatus === 'cancelled' || runStatus === 'expired') {
          const errorMessage = runData.last_error?.message || `Assistant run ${runStatus}`;
          const errorDetails = runData.last_error
            ? ` - ${runData.last_error.code}: ${runData.last_error.message}`
            : '';
          throw new Error(`${errorMessage}${errorDetails}`);
        }

        if (runStatus === 'requires_action') {
          const requiredAction = runData.required_action;

          if (requiredAction?.type === 'submit_tool_outputs') {
            const toolCalls = requiredAction.submit_tool_outputs?.tool_calls || [];
            const toolOutputs = await Promise.all(
              toolCalls.map(async (toolCall) => {
                if (toolCall.type === 'function') {
                  const result = await executeFunction(toolCall.function);
                  return { tool_call_id: toolCall.id, output: JSON.stringify(result) };
                }
                return { tool_call_id: toolCall.id, output: JSON.stringify({ error: 'Unsupported tool type' }) };
              })
            );
            await submitToolOutputs(activeThreadId, runId, toolOutputs);
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
        statusCheckCount += 1;
      } while (runStatus === 'in_progress' || runStatus === 'queued' || runStatus === 'requires_action');

      let assistantMessage = null;
      for (let retryCount = 0; retryCount < 5; retryCount += 1) {
        const messagesRes = await listMessages(activeThreadId);
        const assistantMessages = messagesRes.data
          .filter((msg) => msg.role === 'assistant')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        assistantMessage = assistantMessages[0];
        if (assistantMessage?.content?.[0]?.text?.value) break;
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }

      if (!assistantMessage?.content?.[0]?.text?.value) {
        throw new Error('No valid assistant response received after multiple attempts');
      }

      const assistantResponse = assistantMessage.content[0].text.value;
      const savedToOpenAI = await addMessageToThread(activeThreadId, {
        role: 'assistant',
        content: assistantResponse
      });
      const savedToDirectus = await saveChatMessage(activeSessionId, {
        role: 'assistant',
        content: assistantResponse,
        order: currentMessages.length + 1
      });

      if (!savedToOpenAI || !savedToDirectus) {
        throw new Error('Failed to synchronize message across systems');
      }

      setMessages((prev) => [...prev, {
        text: assistantResponse,
        sender: 'assistant',
        date_created: new Date().toISOString()
      }]);

      await updateChatSession(activeSessionId, {
        title: `${userMessage.slice(0, 50)}...`,
        date_updated: new Date().toISOString()
      });

      fetchSessionsListRef.current({ resetPage: false });
    } catch (error) {
      process.env.NODE_ENV === 'development' && console.error('Error in thread operation:', error);
      setMessages((prev) => [...prev, {
        text: t('assistant.inbox.threadError'),
        sender: 'system',
        date_created: new Date().toISOString()
      }]);
      const newThreadId = await createThreadWithRetry(3, 2000);
      setThreadId(newThreadId);
    } finally {
      setIsProcessing(false);
    }
  }, [input, threadId, adjustTextAreaHeight, fetchSessionsListRef, activeSessionIdRef, t]);

  const handleStopGeneration = useCallback(() => {
    setIsProcessing(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { value } = e.target;
    setInput(value);

    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      const lineCount = (value.match(/\n/g) || []).length + 1;
      const dynamicMaxHeight = Math.min(lineCount * 24 + 16, 300);
      const newHeight = Math.min(textAreaRef.current.scrollHeight, dynamicMaxHeight);
      setTextareaHeight(`${newHeight}px`);
    }
  }, []);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  useEffect(() => {
    smartScrollToLatestMessage();
  }, [messages.length, smartScrollToLatestMessage]);

  return {
    messages,
    chatMetadata,
    isOwner,
    chatLoading,
    sessionSwitchLoading,
    isProcessing,
    isInitializing,
    input,
    textareaHeight,
    textAreaRef,
    messagesEndRef,
    messagesContainerRef,
    selectedSource,
    selectedCitation,
    drawerOpen,
    setDrawerOpen,
    loadedSessionIdRef,
    loadSpecificSession,
    handleCreateNewChat,
    resetSession,
    handleSourceClick,
    handleUserScroll,
    handleInputChange,
    handleKeyPress,
    handleSendMessage,
    handleStopGeneration
  };
};

export default useAssistantChat;
